import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { NotificationBell } from "./NotificationBell";
import { OrgSwitcher } from "./OrgSwitcher";

/**
 * Top-level layout shell. Wraps every authenticated page with a glass-tinted
 * sticky header (logo + primary nav + notifications + sign out). On mobile,
 * primary nav collapses behind a hamburger and the menu slides down.
 *
 * Visual notes:
 *  - Header uses `.glass` so content scrolls through it with a soft blur.
 *  - Active nav links get a thin accent underline plus accent-tinted text.
 *  - Main content fades up on mount, slow enough to feel intentional but
 *    fast enough to never block interaction. Respects prefers-reduced-motion.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, isManager, activeOrg, signOut } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  async function onSignOut() {
    await signOut();
    nav("/login", { replace: true });
  }

  const primaryLinks: { to: string; label: string; show: boolean }[] = [
    { to: "/", label: "Home", show: true },
    { to: "/my-schedule", label: "My schedule", show: true },
    { to: "/preferences", label: "Preferences", show: true },
    { to: "/swaps", label: "Swaps", show: true },
    { to: "/time-off", label: "Time off", show: true },
    { to: "/open-shifts", label: "Open shifts", show: true },
    { to: "/manage", label: "Manage", show: isManager },
    { to: "/admin/users", label: "Admin", show: user?.is_admin === true },
  ];

  function closeMenu() {
    setMenuOpen(false);
  }

  const mainRef = useRef<HTMLElement>(null);

  // Polish v0.4.0 #20: ESC closes the mobile menu. Always-listened so users
  // can use the keyboard even after focus has wandered.
  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Polish v0.4.0 #21: lock body scroll while the mobile menu is open so the
  // page doesn't scroll behind the overlay. The original .glass header is
  // sticky, so without this the body scrolls under the open menu.
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [menuOpen]);

  // Polish v0.4.0 #22: smooth scroll to top + focus #main on every route
  // change so screen-reader / keyboard users land at the new page heading.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMenuOpen(false);
    // Move focus to <main> without grabbing it visually — tabindex=-1 lets
    // .focus() succeed without a focus ring.
    const main = mainRef.current;
    if (main) {
      main.setAttribute("tabindex", "-1");
      main.focus({ preventScroll: true });
    }
  }, [loc.pathname]);

  return (
    <div className="relative min-h-screen">
      {/* Keyboard/screen-reader shortcut past the nav. Visually hidden until
          focused; first tab stop on every page. Target <main id="main"> is
          already focus-managed on route change. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3
                   focus:z-50 focus:rounded-md focus:bg-ink-0 focus:px-4 focus:py-2
                   focus:text-sm focus:font-medium focus:text-accent
                   focus:outline focus:outline-2 focus:outline-accent"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-30 glass border-b-0">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          {/* ----- Logo / wordmark -----
              When an active org is resolved, the wordmark switches to the
              org's name with a small "* Scheduler" suffix as context. Falls
              back to "Yes& Scheduler" for super-admins / pending admins. */}
          <Link
            to="/"
            aria-label={activeOrg ? `${activeOrg.name} home` : "Yes& Scheduler home"}
            className="group flex min-w-0 items-center gap-2.5 text-sm font-semibold tracking-tight text-fg-0"
          >
            <span
              aria-hidden="true"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-md
                         bg-gradient-to-br from-accent to-accent-press
                         text-ink-0 shadow-glow-accent
                         transition-transform duration-300 ease-out
                         group-hover:scale-105"
            >
              <span className="text-[11px] font-bold">S</span>
            </span>
            <span className="hidden truncate sm:inline">
              {activeOrg ? (
                <>
                  <span className="text-fg-0">{activeOrg.name}</span>
                  <span className="ml-1.5 text-fg-3 font-normal">
                    · Scheduler
                  </span>
                </>
              ) : (
                "Yes& Scheduler"
              )}
            </span>
          </Link>

          {/* ----- Desktop nav ----- */}
          <nav className="hidden items-center gap-0.5 md:flex" aria-label="Primary">
            {primaryLinks
              .filter((l) => l.show)
              .map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `relative rounded-md px-3 py-1.5 text-sm font-medium
                     transition-colors duration-200 ease-out
                     ${
                       isActive
                         ? "text-accent"
                         : "text-fg-1 hover:bg-ink-2 hover:text-fg-0"
                     }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {l.label}
                      {isActive ? (
                        <span
                          aria-hidden="true"
                          className="absolute inset-x-3 -bottom-[1px] h-[2px]
                                     bg-gradient-to-r from-accent to-accent-hover
                                     rounded-full shadow-glow-accent"
                        />
                      ) : null}
                    </>
                  )}
                </NavLink>
              ))}
          </nav>

          {/* ----- Right side: org switcher + bell + sign out + hamburger ----- */}
          <div className="flex items-center gap-1.5">
            <OrgSwitcher />
            <NotificationBell />
            <button
              type="button"
              onClick={onSignOut}
              className="hidden text-sm font-medium text-fg-2 transition-colors hover:text-fg-0 sm:inline"
            >
              Sign out
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="grid h-9 w-9 place-items-center rounded-md text-fg-1
                         transition-colors hover:bg-ink-2 hover:text-fg-0 md:hidden"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              {menuOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ----- Mobile menu ----- */}
        {menuOpen ? (
          <div
            id="mobile-menu"
            className="md:hidden border-t border-ink-3
                       motion-safe:animate-fade-in"
          >
            <nav className="mx-auto flex max-w-6xl flex-col gap-0.5 px-3 py-2" aria-label="Mobile">
              {primaryLinks
                .filter((l) => l.show)
                .map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-2.5 text-sm font-medium
                       transition-colors duration-200
                       ${
                         isActive
                           ? "bg-accent/10 text-accent"
                           : "text-fg-1 hover:bg-ink-2 hover:text-fg-0"
                       }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              <NavLink
                to="/profile"
                onClick={closeMenu}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium
                   transition-colors duration-200
                   ${
                     isActive
                       ? "bg-accent/10 text-accent"
                       : "text-fg-1 hover:bg-ink-2 hover:text-fg-0"
                   }`
                }
              >
                Profile
              </NavLink>
              <button
                type="button"
                onClick={onSignOut}
                className="rounded-md px-3 py-2.5 text-left text-sm font-medium
                           text-fg-1 transition-colors hover:bg-ink-2 hover:text-fg-0"
              >
                Sign out
              </button>
            </nav>
          </div>
        ) : null}
      </header>

      <main
        id="main"
        ref={mainRef}
        className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8
                   motion-safe:animate-fade-up
                   focus:outline-none"
        style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}
      >
        {children}
      </main>
    </div>
  );
}
